/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { logError } from 'utils/logger';
import GoogleMapReact from 'google-map-react';

import { GeoLocationIcon } from 'assets/icons';
import { PointOnMapWrap, MarkerWrapper, MapWrapper } from './style';

/**
 * @fileoverview Google Map component for displaying location markers
 *
 * @known-issue findDOMNode Warning
 * This component uses google-map-react which internally uses findDOMNode.
 * This causes React StrictMode warnings in the console:
 * "Warning: findDOMNode is deprecated in StrictMode..."
 *
 * This is a known issue with the google-map-react library and not with our implementation.
 * The warning doesn't affect functionality and can be safely ignored until the library
 * updates its implementation to use modern React patterns.
 *
 * Related discussions:
 * - https://github.com/google-map-react/google-map-react/issues/914
 * - https://github.com/google-map-react/google-map-react/issues/924
 */

const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY;

// Log API key status for debugging
if (!googleApiKey) {
  logError(
    'Google Maps API key is missing',
    new Error('Missing REACT_APP_GOOGLE_API_KEY'),
    { message: 'Set REACT_APP_GOOGLE_API_KEY in .env' }
  );
}

const Marker = ({ lat, lng }) => (
  <PointOnMapWrap>
    <MarkerWrapper $lat={lat} $lng={lng}>
      <GeoLocationIcon />
    </MarkerWrapper>
  </PointOnMapWrap>
);

/**
 * Google Map component that displays a location marker
 * @param {Object} props - Component props
 * @param {Object} props.location - Location coordinates
 * @param {number} props.location.lat - Latitude
 * @param {number} props.location.lng - Longitude
 * @returns {React.ReactElement} Map component with marker
 */
const GoogleMap = ({ location }) => {
  // Validate and parse location prop (handles both string and number coordinates)
  if (!location || !location.lat || !location.lng) {
    console.error('GoogleMap: Missing location coordinates', location);
    return (
      <MapWrapper>
        <div style={{ color: '#FF1F8C', textAlign: 'center', padding: '20px' }}>
          Map location unavailable
        </div>
      </MapWrapper>
    );
  }

  // Parse coordinates to numbers (handles string coordinates from API)
  const lat =
    typeof location.lat === 'string' ? parseFloat(location.lat) : location.lat;
  const lng =
    typeof location.lng === 'string' ? parseFloat(location.lng) : location.lng;

  // Validate parsed coordinates
  if (isNaN(lat) || isNaN(lng)) {
    console.error('GoogleMap: Invalid coordinate values', {
      lat,
      lng,
      original: location,
    });
    return (
      <MapWrapper>
        <div style={{ color: '#FF1F8C', textAlign: 'center', padding: '20px' }}>
          Invalid map coordinates
        </div>
      </MapWrapper>
    );
  }

  const handleApiLoaded = (map) => {
    // Force map to resize after load to ensure tiles render
    if (map && typeof map.setCenter === 'function') {
      setTimeout(() => {
        // eslint-disable-next-line no-undef
        if (typeof google !== 'undefined' && google.maps && google.maps.event) {
          // eslint-disable-next-line no-undef
          google.maps.event.trigger(map, 'resize');
          map.setCenter({ lat, lng });
        }
      }, 100);
    }
  };

  // Ensure we pass proper object format to GoogleMapReact with parsed numbers
  const center = { lat, lng };

  return (
    <MapWrapper>
      {/* @ts-expect-error - GoogleMapReact uses findDOMNode internally */}
      <GoogleMapReact
        bootstrapURLKeys={{
          key: googleApiKey,
          libraries: ['places', 'geometry'],
        }}
        center={center}
        defaultCenter={center}
        defaultZoom={15}
        onGoogleApiLoaded={({ map }) => handleApiLoaded(map)}
        options={{
          fullscreenControl: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          styles: [], // Ensure default styles
        }}
        yesIWantToUseGoogleMapApiInternals
      >
        <Marker lat={lat} lng={lng} />
      </GoogleMapReact>
    </MapWrapper>
  );
};

export default GoogleMap;
